import type { IntlShape } from 'react-intl';
import getHelpCenterUrl from '@alltrails/core/utils/getHelpCenterUrl';
import { StatusBadgeProps } from '@alltrails/denali/src/components/StatusBadge/StatusBadge';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { User } from '@alltrails/types/User';
import localizedPaths from './localizedPaths';

export type FooterLinksType = {
  href: string;
  title: string;
  amplitudeEventName?: string;
  childLinks: {
    amplitudeEventName?: string;
    href: string;
    openInNewWindow?: boolean;
    title: string;
    badge?: StatusBadgeProps;
  }[];
};

type TranslateRouteCategory = 'trails' | 'parks' | 'cities' | 'regions';

const translateRoute = (languageRegionCode: LanguageRegionCode, category: TranslateRouteCategory): string => {
  const routeMap = localizedPaths[languageRegionCode];
  return (routeMap && routeMap[category]) || category;
};

const exploreLinks = (intl: IntlShape, languageRegionCode: LanguageRegionCode): FooterLinksType => ({
  title: intl.formatMessage({ defaultMessage: 'Explore' }),
  href: '/explore',
  childLinks: [
    {
      title: intl.formatMessage({ defaultMessage: 'Countries' }),
      href: '/directory/countries'
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Regions' }),
      href: `/directory/${translateRoute(languageRegionCode, 'regions')}/A/1`
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Cities' }),
      href: `/directory/${translateRoute(languageRegionCode, 'cities')}/A/1`
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Parks' }),
      href: `/directory/${translateRoute(languageRegionCode, 'parks')}/A/1`
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Trails' }),
      href: `/directory/${translateRoute(languageRegionCode, 'trails')}/A/1`
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Points of Interest' }),
      href: '/directory/poi/A/1'
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Trail Features' }),
      href: '/directory/trail-features'
    }
  ]
});

const mapLinks = ({
  intl,
  currentUser
}: {
  intl: IntlShape;
  currentUser?: User | null;
}): FooterLinksType => ({
  title: intl.formatMessage({ defaultMessage: 'Custom routes & maps' }),
  href: currentUser
    ? `/members/${currentUser.slug}/custom-routes`
    : // 'user-custom-routes=true' is used to redirect the user to their custom routes page after they authenticate:
      `/signup?returnTo=${encodeURIComponent('/explore?user-custom-routes=true')}`,
  childLinks: [
    {
      title: intl.formatMessage({ defaultMessage: 'Build custom route' }),
      // 'trigger=footer' is used to pass the correct trigger and upsell trigger to the SignUpModal
      href: '/explore/custom-routes/new?trigger=footer'
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Print maps' }),
      href: currentUser?.featureEntitlements?.includes('print_maps') ? '/maps/print' : '/plus'
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Route converter' }),
      href: '/converter'
    }
  ]
});

const companyLinks = (intl: IntlShape): FooterLinksType => ({
  href: '/about',
  title: intl.formatMessage({ defaultMessage: 'Company' }),
  childLinks: [
    {
      href: '/about',
      title: intl.formatMessage({ defaultMessage: 'About' })
    },
    {
      href: '/careers',
      title: intl.formatMessage({ defaultMessage: 'Jobs' })
    },
    {
      href: '/press',
      title: intl.formatMessage({ defaultMessage: 'News' })
    },
    {
      href: '/ambassadors',
      title: intl.formatMessage({ defaultMessage: 'Ambassadors' })
    },
    {
      href: '/affiliates',
      title: intl.formatMessage({ defaultMessage: 'Affiliates' })
    }
  ]
});

const gearShopLink = process.env.NEXT_PUBLIC_GEAR_SHOP_LINK;

const communityLinks = (languageRegionCode: LanguageRegionCode, intl: IntlShape, irclickid?: string): FooterLinksType => ({
  href: '/community',
  title: intl.formatMessage({ defaultMessage: 'Community' }),
  amplitudeEventName: 'web global footer community clicked',
  childLinks: [
    {
      amplitudeEventName: 'support footer clicked',
      href: getHelpCenterUrl(languageRegionCode),
      title: intl.formatMessage({ defaultMessage: 'Support' })
    },
    {
      amplitudeEventName: 'give pro footer clicked',
      href: '/gift',
      title: intl.formatMessage({ defaultMessage: 'Gift membership' })
    },
    {
      amplitudeEventName: 'gear footer clicked',
      href: `${gearShopLink}${irclickid ? `?${new URLSearchParams({ irclickid })}` : ''}`,
      openInNewWindow: true,
      title: intl.formatMessage({ defaultMessage: 'AllTrails Gear' })
    }
  ]
});

const footerLinks = ({
  intl,
  languageRegionCode,
  user,
  irclickid
}: {
  intl: IntlShape;
  languageRegionCode: LanguageRegionCode;
  user?: User | null;
  irclickid?: string;
}): FooterLinksType[] => [
  exploreLinks(intl, languageRegionCode),
  mapLinks({ intl, currentUser: user }),
  companyLinks(intl),
  communityLinks(languageRegionCode, intl, irclickid)
];

export default footerLinks;
